import React, { useEffect } from "react";
import { useI18n } from "@/i18n";
import ContactForm from "@/components/ContactForm";
import { ArrowRight, Building2, LineChart, ShieldCheck, MapPin, Users, FileText, Compass } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1589909202802-8f4aadce1849?auto=format&fit=crop&w=1600&q=80";
const SKYLINE_IMG = "https://images.unsplash.com/photo-1589909202802-8f4aadce1849?auto=format&fit=crop&w=1400&q=80";
const BUILDING_1 = "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=1200&q=80";
const BUILDING_2 = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80";
const BUILDING_3 = "https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&w=1200&q=80";
const BUILDING_4 = "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=1200&q=80";
const DASHBOARD_IMG = "https://images.unsplash.com/photo-1487958449943-2429e8be8625?auto=format&fit=crop&w=1400&q=80";

const PROJECT_IMAGES = [BUILDING_1, BUILDING_2, BUILDING_3, BUILDING_4];
const VALUE_ICONS = [ShieldCheck, Compass, LineChart, FileText];

export default function Home() {
  const { t, raw, lang } = useI18n();

  useEffect(() => {
    document.title = t("meta.title");
    document.documentElement.lang = lang === "en" ? "en" : "es";
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", t("meta.description"));
  }, [t, lang]);

  // Scroll to hash after route load
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    }
  }, []);

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const projects = raw.projects.list;
  const values = raw.values.items;

  return (
    <main className="home">
      {/* HERO */}
      <section className="hero" data-testid="home-hero">
        <div className="hero-media" aria-hidden="true">
          <img src={HERO_IMG} alt="" />
          <div className="hero-media-tint" />
        </div>
        <div className="container hero-content">
          <span className="kicker">{t("hero.kicker")}</span>
          <h1 className="display">{t("hero.title")}</h1>
          <p className="hero-lead">{t("hero.lead")}</p>
          <div className="hero-actions">
            <a href="#proyectos" onClick={scrollTo("proyectos")} className="btn btn-primary btn-lg" data-testid="hero-cta-primary">
              {t("cta.primary")} <ArrowRight size={16} />
            </a>
            <a href="#contacto" onClick={scrollTo("contacto")} className="btn btn-outline-light btn-lg" data-testid="hero-cta-secondary">
              {t("cta.secondary")}
            </a>
          </div>
          <ul className="hero-badges">
            <li><span className="dot" /> {t("hero.badge1")}</li>
            <li><span className="dot" /> {t("hero.badge2")}</li>
            <li><span className="dot" /> {t("hero.badge3")}</li>
          </ul>
        </div>
      </section>

      {/* STATS */}
      <section className="stats" aria-label="stats">
        <div className="container stats-grid">
          <div><strong>128</strong><span>{t("stats.projects")}</span></div>
          <div><strong>14</strong><span>{t("stats.cities")}</span></div>
          <div><strong>9.400+</strong><span>{t("stats.participants")}</span></div>
          <div><strong>100%</strong><span>{t("stats.transparency")}</span></div>
        </div>
      </section>

      {/* INTRO */}
      <section className="intro" id="nosotros">
        <div className="container intro-grid">
          <div className="intro-copy">
            <span className="eyebrow">01</span>
            <h2 className="section-title">{t("intro.heading")}</h2>
            <p>{t("intro.p1")}</p>
            <p>{t("intro.p2")}</p>
            <p>{t("intro.p3")}</p>
            <p>{t("intro.p4")}</p>
          </div>
          <div className="intro-media">
            <img src={SKYLINE_IMG} alt="Buenos Aires skyline" loading="lazy" />
            <div className="floating-card">
              <Building2 size={18} />
              <div>
                <strong>Puerto Madero</strong>
                <span>Torres residenciales · CABA</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW */}
      <section className="how" id="como-funciona">
        <div className="container">
          <div className="section-head">
            <span className="eyebrow">02</span>
            <h2 className="section-title">{t("how.heading")}</h2>
          </div>
          <div className="how-grid">
            <div className="how-steps">
              {["s1", "s2", "s3", "s4"].map((k) => (
                <div className="how-step" key={k}>
                  <h3>{t(`how.steps.${k}.title`)}</h3>
                  <p>{t(`how.steps.${k}.body`)}</p>
                </div>
              ))}
            </div>
            <div className="how-copy">
              <p>{t("how.p1")}</p>
              <p>{t("how.p2")}</p>
              <p>{t("how.p3")}</p>
              <p>
                {t("how.p4Prefix")} <strong>{t("how.p4Highlight")}</strong>{t("how.p4Suffix")}
              </p>
              <p>{t("how.p5")}</p>
              <p>{t("how.p6")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section className="projects" id="proyectos">
        <div className="container">
          <div className="section-head">
            <span className="eyebrow">03</span>
            <h2 className="section-title">{t("projects.heading")}</h2>
            <p className="section-lead">{t("projects.lead")}</p>
          </div>
          <div className="projects-grid">
            {projects.map((p, i) => (
              <article className="project-card" key={i}>
                <div className="project-thumb">
                  <img src={PROJECT_IMAGES[i % PROJECT_IMAGES.length]} alt={p.title} loading="lazy" />
                  <span className="project-type">{p.type}</span>
                </div>
                <div className="project-body">
                  <h3>{p.title}</h3>
                  <p className="project-city"><MapPin size={12} /> {p.city}</p>
                  <p>{p.body}</p>
                  <a href="#contacto" onClick={scrollTo("contacto")} className="project-link">
                    {t("cta.learnMore")} <ArrowRight size={14} />
                  </a>
                </div>
              </article>
            ))}
          </div>
          <p className="projects-disclaimer">{t("projects.disclaimer")}</p>
        </div>
      </section>

      {/* VALUES */}
      <section className="values">
        <div className="container values-grid">
          <div className="values-media">
            <img src={DASHBOARD_IMG} alt="Panel de seguimiento" loading="lazy" />
          </div>
          <div className="values-copy">
            <span className="eyebrow">04</span>
            <h2 className="section-title">{t("values.heading")}</h2>
            <ul className="values-list">
              {values.map((v, i) => {
                const Icon = VALUE_ICONS[i % VALUE_ICONS.length];
                return (
                  <li key={i}>
                    <span className="values-icon"><Icon size={18} strokeWidth={1.6} /></span>
                    <div>
                      <h3>{v.title}</h3>
                      <p>{v.body}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="contact" id="contacto">
        <div className="container contact-grid">
          <div className="contact-copy">
            <span className="eyebrow">05</span>
            <h2 className="section-title">{t("contact.heading")}</h2>
            <p>{t("contact.lead")}</p>
            <ul className="contact-info">
              <li><Users size={16} /> {t("footer.address.email")}</li>
              <li><MapPin size={16} /> {t("footer.address.line1")}, {t("footer.address.line2")}</li>
            </ul>
          </div>
          <div className="contact-card">
            <ContactForm />
          </div>
        </div>
      </section>
    </main>
  );
}
